import React, { useMemo, useState } from "react";
import {
  Car,
  Search,
  SlidersHorizontal,
  ArrowRight,
  BadgeCheck,
  Sparkles,
  Phone,
  IdCard,
  Tag,
} from "lucide-react";
// --- Tailwind UI primitives (no shadcn) ---

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "secondary" | "outline" | "ghost";
};

const Button: React.FC<ButtonProps> = ({ variant = "default", className = "", ...props }) => {
  const base = "inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold transition rounded-xl focus:outline-none";
  const variants: Record<string, string> = {
    default: "bg-white text-black hover:bg-white/90",
    secondary: "bg-white/10 text-white hover:bg-white/15",
    outline: "border border-white/20 text-white hover:bg-white/10",
    ghost: "text-white hover:bg-white/10",
  };
  return <button className={`${base} ${variants[variant]} ${className}`} {...props} />;
};

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className = "", ...props }, ref) => (
    <input
      ref={ref}
      className={`w-full px-3 py-2 text-sm rounded-xl bg-transparent border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 ${className}`}
      {...props}
    />
  )
);
Input.displayName = "Input";

const Card: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = "", ...props }) => (
  <div className={`bg-white/5 border border-white/10 ${className}`} {...props} />
);

const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = "", ...props }) => (
  <div className={className} {...props} />
);

const Badge: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({ className = "", ...props }) => (
  <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full ${className}`} {...props} />
);


type Flow = "sell" | "market"; // Sell = price my car. Market = browse inventory.
type OfferView = "buy" | "lease" | "both";

type CarListing = {
  id: string;
  title: string;
  year: number;
  km: number;
  fuel: "EV" | "Hybrid" | "Petrol" | "Diesel";
  transmission: "Auto" | "Manual";
  brand: string;
  badge?: "Premium" | "Outlet";
  img: string;
  buyPrice?: number; // NOK
  leaseMonthly?: number; // NOK / month
  leaseFromMonths?: number;
};

function assert(cond: unknown, msg: string): void {
  if (!cond) {
    // eslint-disable-next-line no-console
    console.error("Assertion failed:", msg);
  }
}

export default function Car24StoreHome({handleLogout}: {handleLogout: () => void}) {
  // Popup-office identity (matching your screenshot)
  const brand = {
    orange: "#F36B21",
    charcoal: "#0B0F16",
    slate: "#0F172A",
    soft: "#0B0F16",
  };

  // Glassy dark surfaces like the hero in your image
  const ui = {
    glass: "bg-white/5 border-white/10 backdrop-blur",
    glass2: "bg-white/6 border-white/10 backdrop-blur",
    glass3: "bg-white/8 border-white/10 backdrop-blur",
    textMuted: "text-white/70",
    textSoft: "text-white/80",
  };

  const money = (n: number) => new Intl.NumberFormat("no-NO").format(n) + " kr";

  const listings: CarListing[] = useMemo(
    () => [
      {
        id: "l1",
        title: "Tesla Model 3 Long Range",
        year: 2022,
        km: 42000,
        fuel: "EV",
        transmission: "Auto",
        brand: "Tesla",
        badge: "Premium",
        img: "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?auto=format&fit=crop&w=1200&q=70",
        buyPrice: 349000,
        leaseMonthly: 4690,
        leaseFromMonths: 12,
      },
      {
        id: "l2",
        title: "VW ID.4 Pro",
        year: 2021,
        km: 54000,
        fuel: "EV",
        transmission: "Auto",
        brand: "Volkswagen",
        badge: "Premium",
        img: "../VW ID4 Pro.jpeg",
        buyPrice: 289000,
        leaseMonthly: 3990,
        leaseFromMonths: 12,
      },
      {
        id: "l3",
        title: "BMW 3-serie 320d",
        year: 2019,
        km: 88000,
        fuel: "Diesel",
        transmission: "Auto",
        brand: "BMW",
        badge: "Outlet",
        img: "BMW series 3.jpeg",
        buyPrice: 259000,
        leaseMonthly: 3490,
        leaseFromMonths: 12,
      },
      {
        id: "l4",
        title: "Audi A4 45 TFSI quattro",
        year: 2020,
        km: 61000,
        fuel: "Petrol",
        transmission: "Auto",
        brand: "Audi",
        badge: "Premium",
        img: "https://images.unsplash.com/photo-1542362567-b07e54358753?auto=format&fit=crop&w=1200&q=70",
        buyPrice: 429000,
        leaseMonthly: 5490,
        leaseFromMonths: 12,
      },
      {
        id: "l5",
        title: "Toyota RAV4 Hybrid",
        year: 2021,
        km: 52000,
        fuel: "Hybrid",
        transmission: "Auto",
        brand: "Toyota",
        img: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=70",
        buyPrice: 399000,
        leaseMonthly: 5190,
        leaseFromMonths: 12,
      },
      {
        id: "l6",
        title: "Skoda Octavia 1.5 TSI",
        year: 2020,
        km: 74000,
        fuel: "Petrol",
        transmission: "Manual",
        brand: "Skoda",
        img: "https://images.unsplash.com/photo-1525609004556-c46c7d6cf023?auto=format&fit=crop&w=1200&q=70",
        buyPrice: 239000,
        leaseMonthly: 2990,
        leaseFromMonths: 12,
      },
    ],
    []
  );

  // Extra runtime checks (“test cases”)
  useMemo(() => {
    assert(listings.length >= 6, "Expected at least 6 demo listings");
    for (const l of listings) {
      assert(!!l.id, "Listing id must be non-empty");
      assert(!!l.title, "Listing title must be non-empty");
      assert(Number.isFinite(l.year) && l.year >= 1980, "Year should look sane");
      assert(Number.isFinite(l.km) && l.km >= 0, "Km must be >= 0");
      assert(typeof l.img === "string" && l.img.startsWith("http"), "Image must be a URL");
      assert(
        (l.buyPrice == null || (Number.isFinite(l.buyPrice) && l.buyPrice > 0)) &&
          (l.leaseMonthly == null || (Number.isFinite(l.leaseMonthly) && l.leaseMonthly > 0)),
        "Prices must be positive numbers"
      );
    }
    return null;
  }, [listings]);

  // Top-level flow
  const [flow, setFlow] = useState<Flow>("market");

  // Sell onboarding (Vegvesen + Vipps/BankID)
  const [regnr, setRegnr] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [useVipps, setUseVipps] = useState<boolean>(true);

  // Market controls
  const [offerView, setOfferView] = useState<OfferView>("both");
  const [q, setQ] = useState<string>("");
  const [sort, setSort] = useState<"new" | "priceLow" | "priceHigh">("new");
  const [showSold, setShowSold] = useState<boolean>(false);

  const [quality, setQuality] = useState<{ Premium: boolean; Outlet: boolean }>({ Premium: true, Outlet: true });
  const [brands, setBrands] = useState<Record<string, boolean>>({
    Audi: true,
    BMW: true,
    Tesla: true,
    Volkswagen: true,
    Toyota: true,
    Skoda: true,
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    let list = listings.filter((l) => {
      const okQ = !needle || l.title.toLowerCase().includes(needle) || l.brand.toLowerCase().includes(needle);
      const okQuality = l.badge ? (l.badge === "Premium" ? quality.Premium : quality.Outlet) : true;
      const okBrand = brands[l.brand] ?? true;
      return okQ && okQuality && okBrand;
    });

    if (sort === "priceLow") {
      list = [...list].sort((a, b) => (a.buyPrice ?? 9e15) - (b.buyPrice ?? 9e15));
    } else if (sort === "priceHigh") {
      list = [...list].sort((a, b) => (b.buyPrice ?? 0) - (a.buyPrice ?? 0));
    }

    // showSold is a UI placeholder in this demo
    if (showSold) list = list;

    return list;
  }, [listings, q, sort, showSold, quality, brands]);

  const onGetPrice = () => {
    const reg = regnr.trim().toUpperCase();
    const mob = phone.trim();

    if (!reg) return alert("Reg.nr mangler");
    if (!mob) return alert("Mobilnummer mangler");

    const method = useVipps ? "Vipps" : "BankID";
    alert(
      `Demo: Fetch vehicle data via Vegvesen API for ${reg}. Identify customer via ${method} using mobile ${mob}. Return price estimate + next steps.`
    );
  };

  const Seg = ({
    value,
    onChange,
    options,
  }: {
    value: string;
    onChange: (v: any) => void;
    options: string[];
  }) => (
    <div className={`grid grid-cols-3 rounded-2xl border ${ui.glass} p-1 shadow-sm`}>
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            className="px-3 py-2 rounded-xl text-sm font-semibold transition"
            style={
              active
                ? { background: brand.charcoal, color: "white" }
                : { color: "rgba(255,255,255,0.75)" }
            }
            onClick={() => onChange(o)}
          >
            {o}
          </button>
        );
      })}
    </div>
  );

  const OfferPills = ({ l }: { l: CarListing }) => {
    const buy = l.buyPrice != null;
    const lease = l.leaseMonthly != null;

    if (offerView === "buy") {
      return (
        <div className="mt-3">
          <div className="text-[11px] text-white/55">Buy price</div>
          <div className="text-xl font-bold text-white">{buy ? money(l.buyPrice!) : "–"}</div>
        </div>
      );
    }

    if (offerView === "lease") {
      return (
        <div className="mt-3">
          <div className="text-[11px] text-white/55">Leasing</div>
          <div className="text-xl font-bold text-white">
            {lease ? money(l.leaseMonthly!) : "–"}
            <span className="text-sm font-semibold text-white/55">/mo</span>
          </div>
          <div className="text-[11px] text-white/55">From {l.leaseFromMonths ?? 12} months</div>
        </div>
      );
    }

    return (
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className={`rounded-2xl border ${ui.glass2} p-3`}>
          <div className="text-[11px] text-white/55">Buy</div>
          <div className="text-base font-bold text-white">{buy ? money(l.buyPrice!) : "–"}</div>
        </div>
        <div className={`rounded-2xl border ${ui.glass2} p-3`}>
          <div className="text-[11px] text-white/55">Lease</div>
          <div className="text-base font-bold text-white">
            {lease ? money(l.leaseMonthly!) : "–"}
            <span className="text-xs font-semibold text-white/55">/mo</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: brand.soft,
      }}
    >
      {/* Background glow/noise similar to your screenshot */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 500px at 20% 15%, rgba(243,107,33,0.12), transparent 60%), radial-gradient(700px 380px at 70% 30%, rgba(56,189,248,0.08), transparent 60%), radial-gradient(900px 600px at 50% 90%, rgba(255,255,255,0.06), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            background:
              "url(data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='.45'/%3E%3C/svg%3E)",
          }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 backdrop-blur bg-black/25">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-2xl grid place-items-center shadow-sm"
              style={{ background: brand.orange }}
            >
              <Car className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-semibold">Bilsalg24</div>
              <div className="text-white/60 text-xs">Popup-kontorer + nettbutikk</div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <Button
              variant={flow === "market" ? "default" : "secondary"}
              className="rounded-2xl"
              style={flow === "market" ? { background: brand.orange } : {}}
              onClick={() => setFlow("market")}
            >
              Kjøp / Leasing
            </Button>
            <Button
              variant={flow === "sell" ? "default" : "secondary"}
              className="rounded-2xl"
              style={flow === "sell" ? { background: brand.orange } : {}}
              onClick={() => setFlow("sell")}
            >
              Få pris
            </Button>
            <Button onClick={handleLogout} variant="ghost" className="rounded-2xl text-white hover:bg-white/10">
              Logg out
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 pt-8 pb-6">
        <div className={`rounded-[28px] overflow-hidden border ${ui.glass3} shadow-[0_16px_44px_rgba(2,6,23,0.45)]`}>
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-7 p-6 md:p-8">
              <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 border ${ui.glass}`}>
                <Sparkles className="h-4 w-4" style={{ color: brand.orange }} />
                <span className="text-white/80 text-sm">Popup-kontorer + nettbutikk = superrask bilhandel</span>
              </div>

              <h1 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-white">
                {flow === "sell" ? (
                  <>
                    Selg bilen din <span style={{ color: brand.orange }}>på minutter</span>
                  </>
                ) : (
                  <>
                    Kjøp eller lease bil med <span style={{ color: brand.orange }}>samme kort</span>
                  </>
                )}
              </h1>

              <p className={`mt-3 ${ui.textMuted} text-base md:text-lg leading-relaxed`}>
                {flow === "sell"
                  ? "Få et prisestimat direkte. Book inspeksjon i popup-kontor eller hos partner. Oppgjør raskt – vi tar prosessen."
                  : "Listevisning i Rebil-stil – men med våre mørke popup-farger, hvite fonter og oransje CTA. Bytt mellom kjøp, leasing eller begge priser."}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Badge className={`rounded-full ${ui.glass} text-white/80 border`}>
                  <BadgeCheck className="h-3.5 w-3.5 mr-1" style={{ color: brand.orange }} /> Oppgjør innen 24t*
                </Badge>
                <Badge className={`rounded-full ${ui.glass} text-white/80 border`}>
                  <BadgeCheck className="h-3.5 w-3.5 mr-1" style={{ color: brand.orange }} /> Trygg handel &amp; dokumentasjon
                </Badge>
                <Badge className={`rounded-full ${ui.glass} text-white/80 border`}>
                  <Tag className="h-3.5 w-3.5 mr-1" style={{ color: brand.orange }} /> Fast pris – ingen pruting
                </Badge>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <Button className="rounded-2xl" style={{ background: brand.orange }} onClick={() => setFlow("sell")}>
                  Få prisestimat <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button variant="outline" className="rounded-2xl" onClick={() => setFlow("market")}>
                  Se biler
                </Button>
              </div>

              {flow === "sell" && (
                <div className={`mt-6 rounded-3xl border ${ui.glass} p-5`}>
                  <div className="text-white font-semibold">Få pris på bilen</div>
                  <div className="mt-1 text-xs text-white/55">
                    Reg.nr → Vegvesen lookup → identifiser via Vipps mobil (fallback BankID)
                  </div>

                  <div className="mt-4 grid md:grid-cols-12 gap-3">
                    <div className="md:col-span-4">
                      <label className="text-xs text-white/70">Reg.nr</label>
                      <Input
                        value={regnr}
                        onChange={(e) => setRegnr(e.target.value.toUpperCase())}
                        placeholder="AB12345"
                        className="mt-1 rounded-2xl bg-white/10 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div className="md:col-span-4">
                      <label className="text-xs text-white/70">Mobil</label>
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="9xx xx xxx"
                        className="mt-1 rounded-2xl bg-white/10 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div className="md:col-span-4">
                      <label className="text-xs text-white/70">Identifisering</label>
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setUseVipps(true)}
                          className="rounded-2xl px-3 py-2 text-sm border border-white/10 transition flex items-center justify-center gap-2"
                          style={
                            useVipps
                              ? { background: brand.orange, borderColor: brand.orange, color: "white" }
                              : { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.85)" }
                          }
                        >
                          <Phone className="h-4 w-4" /> Vipps
                        </button>
                        <button
                          type="button"
                          onClick={() => setUseVipps(false)}
                          className="rounded-2xl px-3 py-2 text-sm border border-white/10 transition flex items-center justify-center gap-2"
                          style={
                            !useVipps
                              ? { background: brand.orange, borderColor: brand.orange, color: "white" }
                              : { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.85)" }
                          }
                        >
                          <IdCard className="h-4 w-4" /> BankID
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button className="rounded-2xl" style={{ background: brand.orange }} onClick={onGetPrice}>
                      Få prisestimat <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    <Button variant="outline" className="rounded-2xl" onClick={() => alert("Demo: Finn popup-kontor")}
                    >
                      Finn popup-kontor
                    </Button>
                    <span className="text-xs text-white/50">*Avhenger av inspeksjon og tid på døgnet.</span>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-5 relative min-h-[260px]">
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, ${brand.charcoal} 0%, ${brand.slate} 60%, ${brand.orange} 220%)`,
                }}
              />
              <div className="absolute inset-0 opacity-70">
                <img
                  src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1400&q=70"
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-black/35" />
              <div className={`absolute bottom-4 left-4 right-4 rounded-3xl p-4 border ${ui.glass} `}>
                <div className="text-white font-semibold">"Popup office"-farger</div>
                <div className="text-white/70 text-sm mt-1">
                  Mørk grafitt + signatur-oransje – samme uttrykk som kontorene.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace */}
      <main className="mx-auto max-w-7xl px-4 pb-16">
        {flow === "market" && (
          <section className="mt-3">
            <div className="grid lg:grid-cols-12 gap-4">
              {/* Sidebar filters */}
              <Card className={`lg:col-span-3 rounded-3xl overflow-hidden border ${ui.glass2} shadow-sm`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-white">Filtre</div>
                    <SlidersHorizontal className="h-4 w-4 text-white/40" />
                  </div>

                  <div className="mt-4">
                    <div className="text-xs font-semibold text-white/70">Kvalitetsklasse</div>
                    <div className="mt-2 grid gap-2">
                      {(["Premium", "Outlet"] as const).map((k) => (
                        <label key={k} className="flex items-center gap-2 text-sm text-white/80">
                          <input
                            type="checkbox"
                            checked={quality[k]}
                            onChange={(e) => setQuality((p) => ({ ...p, [k]: e.target.checked }))}
                          />
                          {k}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="text-xs font-semibold text-white/70">Merke</div>
                    <div className="mt-2 grid gap-2 max-h-56 overflow-auto pr-1">
                      {Object.keys(brands).map((b) => (
                        <label key={b} className="flex items-center gap-2 text-sm text-white/80">
                          <input
                            type="checkbox"
                            checked={brands[b]}
                            onChange={(e) => setBrands((p) => ({ ...p, [b]: e.target.checked }))}
                          />
                          {b}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6">
                    <Button
                      className="w-full rounded-2xl"
                      style={{ background: brand.orange }}
                      onClick={() => alert("Demo: Apply filters")}
                    >
                      Bruk filtre
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full rounded-2xl mt-2"
                      onClick={() => {
                        setQuality({ Premium: true, Outlet: true });
                        setBrands(
                          (p) =>
                            Object.fromEntries(Object.keys(p).map((k) => [k, true])) as Record<string, boolean>
                        );
                      }}
                    >
                      Nullstill
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Main list */}
              <div className="lg:col-span-9">
                <Card className={`rounded-3xl border ${ui.glass2} shadow-sm`}>
                  <CardContent className="p-5">
                    <div className="grid md:grid-cols-12 gap-3 items-end">
                      <div className="md:col-span-6">
                        <div className="text-xs font-semibold text-white/70">Søk</div>
                        <div className="relative mt-1">
                          <Search className="h-4 w-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                          <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={`Søk blant ${listings.length} biler`}
                            className="pl-9 rounded-2xl bg-white/10 border-white/10 text-white placeholder:text-white/40"
                          />
                        </div>
                      </div>

                      <div className="md:col-span-3">
                        <div className="text-xs font-semibold text-white/70">Sorter</div>
                        <select
                          value={sort}
                          onChange={(e) => setSort(e.target.value as any)}
                          className="mt-1 w-full rounded-2xl border border-white/10 px-3 py-2 text-sm bg-white/10 text-white"
                        >
                          <option value="new">Nylig lagt til</option>
                          <option value="priceLow">Pris: lav → høy</option>
                          <option value="priceHigh">Pris: høy → lav</option>
                        </select>
                      </div>

                      <div className="md:col-span-3">
                        <div className="text-xs font-semibold text-white/70">Visning</div>
                        <div className="mt-1">
                          <Seg value={offerView} onChange={(v) => setOfferView(v)} options={["buy", "lease", "both"]} />
                        </div>
                        <label className="mt-2 flex items-center gap-2 text-xs text-white/70">
                          <input type="checkbox" checked={showSold} onChange={(e) => setShowSold(e.target.checked)} /> Vis solgte biler
                        </label>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="mt-4 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {/* Inline promo card */}
                  <Card className={`rounded-3xl overflow-hidden border ${ui.glass2} shadow-sm`}>
                    <div
                      className="p-6 min-h-[340px] flex flex-col justify-between"
                      style={{
                        background: `linear-gradient(135deg, ${brand.charcoal} 0%, ${brand.slate} 60%, ${brand.orange} 240%)`,
                      }}
                    >
                      <div>
                        <div className="text-white/80 text-sm font-semibold">Selg bilen din</div>
                        <div className="mt-2 text-2xl font-semibold text-white">Få estimat direkte</div>
                        <div className="mt-2 text-white/70 text-sm">
                          Reg.nr → Vegvesen lookup. Identifiser via Vipps mobil (fallback BankID).
                        </div>
                      </div>
                      <div>
                        <Input
                          value={regnr}
                          onChange={(e) => setRegnr(e.target.value.toUpperCase())}
                          placeholder="REG.NR"
                          className="rounded-2xl bg-white/10 border-white/15 text-white placeholder:text-white/50"
                        />
                        <Button
                          className="mt-3 w-full rounded-2xl"
                          style={{ background: brand.orange }}
                          onClick={() => setFlow("sell")}
                        >
                          Neste steg
                        </Button>
                        <div className="mt-3 text-[11px] text-white/60">Trust signals / reviews kan ligge her.</div>
                      </div>
                    </div>
                  </Card>

                  {filtered.map((l) => (
                    <Card key={l.id} className={`rounded-3xl overflow-hidden border ${ui.glass2} shadow-sm`}>
                      <div className="relative h-44 bg-black/20">
                        <img src={l.img} alt="" className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/0 to-black/0" />

                        <div className="absolute top-3 left-3 flex items-center gap-2">
                          {l.badge && (
                            <span
                              className="text-[11px] px-2 py-1 rounded-full font-semibold border border-white/15"
                              style={{
                                background: "rgba(255,255,255,0.10)",
                                color: "rgba(255,255,255,0.85)",
                              }}
                            >
                              {l.badge.toUpperCase()}
                            </span>
                          )}
                          <span className="text-[11px] px-2 py-1 rounded-full text-white border border-white/20 bg-black/35 backdrop-blur">
                            {l.fuel}
                          </span>
                        </div>
                      </div>

                      <CardContent className="p-5">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-semibold text-white leading-snug">{l.title}</div>
                            <div className="mt-1 text-sm text-white/70">
                              {l.year} • {new Intl.NumberFormat("no-NO").format(l.km)} km • {l.transmission}
                            </div>
                          </div>
                          <div className={`h-10 w-10 rounded-2xl border ${ui.glass} grid place-items-center`}>
                            <Car className="h-4 w-4 text-white/60" />
                          </div>
                        </div>

                        <OfferPills l={l} />

                        <div className="mt-4 flex items-center justify-between gap-2">
                          <Button variant="outline" className="rounded-2xl" onClick={() => alert(`Demo: Open ${l.title}`)}>
                            Detaljer
                          </Button>
                          <Button
                            className="rounded-2xl"
                            style={{ background: brand.orange }}
                            onClick={() => alert(`Demo: Start checkout for ${l.title} (${offerView})`)}
                          >
                            Fortsett
                          </Button>
                        </div>

                        <div className="mt-3 text-[11px] text-white/50">
                          Samme kort støtter både kjøp og leasing. Koble mot inventory + pricing API.
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-white/70">Viser {filtered.length} biler</div>
                  <Button variant="outline" className="rounded-2xl" onClick={() => alert("Demo: Load more")}>
                    Last flere
                  </Button>
                </div>
              </div>
            </div>

            <footer className="mt-10 text-xs text-white/40">
              Demo marketplace UI. Toggle: buy / lease / both.
            </footer>
          </section>
        )}
      </main>
    </div>
  );
}
